import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Button, Icon, Popover, Row } from '@efira/ui';

import { NEWS, NEWS_ARE_EXAMPLES } from './news';
import { ProStatus } from '../ProStatus';
import type { NewsItem } from './news';

/**
 * Панель уведомлений - ДВУХУРОВНЕВАЯ, как у эталона.
 *
 * Первый уровень - список: заголовок, отрывок, время, шеврон. Нажатие не
 * открывает новый экран и не уводит из консоли, а подменяет список полным
 * текстом внутри той же панели; возврат - стрелкой в её шапке.
 *
 * ОТЛИЧИЕ ОТ ЭТАЛОНА, СДЕЛАННОЕ НАМЕРЕННО. У них панель не закрывается по
 * Escape - закрыть можно только повторным нажатием или кликом мимо. Мы берём
 * поведение `HSDropdown`: Escape закрывает. Копировать дефект доступности
 * ради сходства бессмысленно - ворота сходства считают элементы и поведение,
 * а не отсутствие клавиши.
 */
const READ_KEY = 'efira.console.news.read.v1';

function readSeen(): string[] {
  try {
    const raw = window.localStorage.getItem(READ_KEY);
    return raw === null ? [] : (JSON.parse(raw) as string[]);
  } catch {
    return [];
  }
}

/**
 * Свежее - относительно, старое - датой.
 *
 * «4 ч назад» отвечает на вопрос «я это уже видел?», а «18 сентября» - нет.
 * Через неделю всё наоборот, поэтому порог - семь дней.
 */
function formatDate(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const hours = Math.floor((now.getTime() - date.getTime()) / 3_600_000);

  if (hours < 1) return 'только что';
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} дн назад`;

  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(date);
}

export function Notifications() {
  const [open, setOpen] = useState<NewsItem | null>(null);
  const [seen, setSeen] = useState<string[]>([]);
  const backRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /** Уровень уже переключали хотя бы раз: первый показ фокус не трогает. */
  const switched = useRef(false);

  /**
   * ФОКУС ЕДЕТ ЗА УРОВНЕМ ПАНЕЛИ.
   *
   * Переход список → деталь заменяет всё содержимое панели, и элемент, на
   * котором стоял фокус, исчезает: браузер роняет фокус на `body`. Для того,
   * кто ведёт панель с клавиатуры, это значит «панель пропала» - следующий Tab
   * уводит в начало страницы, мимо открытой панели.
   *
   * Поэтому при входе в деталь фокус переносится на кнопку возврата, при
   * возврате - обратно в список.
   */
  useEffect(() => {
    if (!switched.current) return;

    if (open !== null) backRef.current?.focus();
    else listRef.current?.querySelector<HTMLElement>('[data-notif-item]')?.focus();
  }, [open]);

  // Прочитанное читается из хранилища событием монтирования наблюдателя, а не
  // в теле эффекта: правило проекта запрещает setState в теле эффекта.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSeen(readSeen());
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, []);

  const unread = NEWS.filter((item) => !seen.includes(item.id)).length;

  const markSeen = (item: NewsItem) => {
    const next = [...new Set([...seen, item.id])];
    setSeen(next);
    try {
      window.localStorage.setItem(READ_KEY, JSON.stringify(next));
    } catch {
      // Непрочитанное, не пережившее перезагрузку, - не повод для ошибки.
    }
  };

  return (
    <Popover
      /*
        Закрытие панели сбрасывает второй уровень: у эталона панель всегда
        открывается списком, а не тем уведомлением, что читали в прошлый раз.
      */
      onOpenChange={(value) => {
        if (!value) setOpen(null);
      }}
      label={unread > 0 ? `Уведомления, непрочитанных ${unread}` : 'Уведомления'}
      /* Колокол стоит у левого края окна: панель раскрывается ВПРАВО, иначе
         её левая половина уезжает за край экрана вместе с кнопкой возврата. */
      placement="bottom-start"
      triggerIconOnly
      triggerClassName="relative"
      className="max-h-[70vh] w-96 max-w-[90vw] overflow-y-auto p-0"
      trigger={
        <>
          <Icon name="bell" size="sm" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="bg-primary border-background absolute -end-0.5 -top-0.5 size-2 rounded-full border"
            />
          )}
        </>
      }
    >
      {open === null ? (
        <div data-topbar="notif-list" ref={listRef}>
          <p className="text-foreground border-foreground/10 border-b px-3 py-2 text-sm font-medium">
            Уведомления
          </p>
          {NEWS_ARE_EXAMPLES && (
            <p className="border-foreground/10 text-foreground/80 border-b px-3 py-2 text-sm">
              Примеры. Лента новостей подключится вместе с публикацией релизов.
            </p>
          )}
          <ul>
            {/*
              СТРОКА УВЕДОМЛЕНИЯ - РОЛЬ «СТРОКА СПИСКА», то есть `Row`.

              До единицы 17 это была кнопка пресета с тремя строками текста
              внутри. У кнопки фиксированная высота, и строки налезали друг
              на друга: дата одного уведомления ложилась на заголовок
              следующего (аудит единицы 16, находка 02).
            */}
            {NEWS.map((item) => (
              <li key={item.id}>
                <Row
                  asChild
                  data-notif-item={item.id}
                  title={
                    <span className="flex items-center gap-2">
                      {!seen.includes(item.id) && (
                        <span aria-hidden="true" className="bg-primary size-1.5 rounded-full" />
                      )}
                      {item.title}
                      {item.pro === true && <ProStatus />}
                    </span>
                  }
                  description={
                    <>
                      <span className="line-clamp-2 block">{item.snippet}</span>
                      <span className="block">{formatDate(item.date)}</span>
                    </>
                  }
                  actions={<Icon name="chevronRight" size="sm" />}
                >
                  {/* eslint-disable-next-line no-restricted-syntax -- `asChild`
                      строки требует голого элемента; содержимое вкладывает
                      сам `Row`. */}
                  <button
                    type="button"
                    onClick={() => {
                      switched.current = true;
                      setOpen(item);
                      markSeen(item);
                    }}
                  />
                </Row>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div data-topbar="notif-detail">
          <div className="border-foreground/10 flex items-center gap-2 border-b px-2 py-2">
            <Button
              variant="ghost"
              size="s"
              iconOnly
              ref={backRef}
              aria-label="Назад к списку уведомлений"
              onClick={() => {
                switched.current = true;
                setOpen(null);
              }}
            >
              <Icon name="chevronLeft" size="sm" />
            </Button>
            <span className="text-sm font-medium">Уведомление</span>
          </div>
          <div className="px-3 py-3">
            <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold">
              {open.title}
              {open.pro === true && <ProStatus />}
            </h3>
            <p className="text-foreground/80 mb-2 text-sm">{formatDate(open.date)}</p>
            {open.body.split('\n\n').map((paragraph) => (
              <p key={paragraph.slice(0, 24)} className="text-foreground/80 mb-2 text-sm">
                {paragraph}
              </p>
            ))}
            {open.link !== undefined && (
              <Link
                to={open.link.to}
                data-topbar="notif-detail-link"
                data-popover-close
                className="mt-1 gap-2"
              >
                {open.link.title}
                <Icon name="arrowRight" size="sm" />
              </Link>
            )}
          </div>
        </div>
      )}
    </Popover>
  );
}
